<template lang="pug">
#manage-calendar-modal(
	:class="{'grid': manageCalendarState === 'list'}"
	@click.stop
)

	//- Header -//
	header.header.col
		h1 Manage Trainers and Calendars
		.row.tabs
			template(v-for="t in tabs" :key="`t-${t.state}`")
				.header-option(
					:class="{'selected': t.state === manageCalendarState}"
					@click="(manageCalendarState=t.state as typeof manageCalendarState)"
				) {{ t.title }}

	//- Main Calendar Setting -//
	template(v-if="manageCalendarState === 'main'")
		div Main Calendar Management
		template(v-if="!calendarStore.main")
			template(v-if="privateStore.isValid")
				ul
					li(
						v-for="cal in userCalendars"
						:key="`nm-${cal.name}`"
					)
							.calendar-choice
								input(type="radio" @change="performMainSet(cal)")
								span {{ cal.name }}
				.buttons
					button(:disabled="googleLoading" @click="doUnlinkGoogle") Unlink from Google

			template(v-else)
				p Main calendar not set up, and you're not attached to Google.
				.buttons
					button(:disabled="googleLoading" @click="doLinkGoogle") Link to Google

		template(v-else)
			p Current main calendar: {{ calendarStore.main.name }}
			.buttons
				button(
					v-if="calendarStore.userOwnsMain"
					@click="performMainUnset"
				) Unset Main Calendar
				button(
					v-if="privateStore.isValid"
					@click="doUnlinkGoogle"
				) Unlink from Google

	//- Trainers Setting -//
	template(v-else-if="manageCalendarState === 'list'")
		.trainer-table.col
			.header.row
				.em.row Email
				.nn.row Display Name
				.cc.row Color
				.bt.row &nbsp;

			//- Edit Trainer -//
			.table.row(v-for="trainer in trainerList" :key="trainer.$id")

				//- Editting
				template(v-if="editTrainerId === trainer.$id && workingTrainer")
					.em.row {{ trainer.email }}
					.nn.row: input.input-el(v-model="workingTrainer.nickname")
					.cc.row: ColorSelector(
						:color="workingTrainer?.calendarColor"
						state="edit"
						@update:color="updateColor"
					)
					.bt.row: div.trainer-buttons.row
						button.icon.black(:disabled="trainerWorking" @click="submitTrainer")
							include ../../resources/icons/pug-files/save.pug
						button.icon.black(:disabled="trainerWorking" @click="deleteTrainer(trainer.$id)")
							include ../../resources/icons/pug-files/close.pug

				//- Viewing
				template(v-else)
					.em.row {{ trainer.email }}
					.nn.row {{ trainer.nickname }}
					.cc.row: ColorSelector(
						:color="trainer?.calendarColor"
						state="view"
						@update:color="updateColor"
					)
					.bt.row: div.trainer-buttons
						button.icon.black(:disabled="trainerWorking" @click="editTrainer(trainer.$id)")
							include ../../resources/icons/pug-files/edit.pug
						button.icon.black(:disabled="trainerWorking" @click="deleteTrainer(trainer.$id)")
							include ../../resources/icons/pug-files/close.pug

			//- Add New Trainer -//
			.table.row(v-if="addNewTrainer && workingTrainer")
				.em.row: input(v-model="workingTrainer.email")
				.nn.row: input(v-model="workingTrainer.nickname")
				.cc.row: ColorSelector(
						:color="workingTrainer?.calendarColor"
						state="add"
						@update:color="updateColor"
					)
				.bt.row: button.icon.black(:disabled="trainerWorking" @click="submitTrainer")
					include ../../resources/icons/pug-files/save.pug

			//- No Trainer - Fallback and/or empty table --//
			.table.row(v-else)
				.em.row
				.nn.row
				.cc.row
				.bt.row

		.err-messages: p {{ errMessages }}

		.buttons
			button.icon.secondary(:disabled="addNewTrainer || trainerWorking" @click="startNewTrainer") Add Trainer
				include ../../resources/icons/pug-files/add.pug
			button.icon.secondary(v-if="addNewTrainer || trainerWorking" @click="back") Back

</template>

<script setup lang="ts">
import { useGoogleSetup } from '@/composables/useGoogleSetup';
import { useCalendars } from '@/store/useCalendars';
import { usePrivate } from '@/store/usePrivate';
import { useTrainers } from '@/store/useTrainers';
import { getAdminCalendars } from '@/util/functions';
import { dataInstanceToData } from '@busy-human/gearbox';
import { ManagementCalendars, ManagementTrainers } from '@busy-human/k9-library';
import { computed, onMounted, ref } from 'vue';
import ColorSelector from "../sub-component/color-selector.vue";

const tabs = {
	"main":	 { title: "Main", state: "main" },
	"list": { title: "Trainers", state: "list" },
};

const manageCalendarState = ref<"loading" | "main" | "list">("list");
const userCalendarsLoaded = ref(false);
const userCalendars = ref<ManagementCalendars.Model[] | null>(null);
const errMessages = ref<null|string>(null);

// --- Main Calendar Functionality
const calendarStore = useCalendars();
const privateStore = usePrivate();
const { doLinkGoogle, doUnlinkGoogle, loading: googleLoading } = useGoogleSetup();

async function loadUserCalendars() {
	const {data: cals} = await getAdminCalendars();
	userCalendars.value = cals;
	userCalendarsLoaded.value = true;
}
onMounted(async () => {
	await loadUserCalendars();
});

// --- Main Calendar
async function performMainSet(cal: ManagementCalendars.Model) {
	manageCalendarState.value = 'loading';
	await calendarStore.setMainCalendar(cal);
	await calendarStore.refreshCalendars();
	manageCalendarState.value = 'main';
}
async function performMainUnset() {
	manageCalendarState.value = 'loading';
	await calendarStore.clearMain();
	await calendarStore.refreshCalendars();
	manageCalendarState.value = 'main';
}

// --- Trainer List Functionality
const trainerStore = useTrainers();
const trainerList = computed(() => [...trainerStore.trainers.values()].sort((a, b) => a.email.localeCompare(b.email)));

const editTrainerId = ref<string | null>(null);
const addNewTrainer = ref(false);
const workingTrainer = ref<ManagementTrainers.Model | null>(null);
const trainerWorking = ref(false);

function updateColor(color:string|any) {
	if(workingTrainer.value) {
		workingTrainer.value.calendarColor = color;
		console.log( "working trainer set: ", workingTrainer.value);
	} else {
		console.log( "working trainer had no value: ", workingTrainer.value);
	}
};

function startNewTrainer() {
	addNewTrainer.value = true;
	editTrainerId.value = null;
	workingTrainer.value = ManagementTrainers.Defaults();
}

function editTrainer(id: string) {
	const tmp = trainerStore.trainers.get(id);
	if(!tmp) throw new Error("Trainer not found");
	editTrainerId.value = id;
	addNewTrainer.value = false;
	workingTrainer.value = dataInstanceToData(tmp);
}

async function deleteTrainer(id: string) {
	// TODO: Confirmation?
	trainerWorking.value = true;
	await trainerStore.deleteTrainer(id);
	trainerWorking.value = false;
}

async function submitTrainer() {

	try {
		if(!workingTrainer.value) throw new Error("No details");
		trainerWorking.value = true;
		if(editTrainerId.value) {
			await trainerStore.updateTrainer(editTrainerId.value, workingTrainer.value);
		} else {
			await trainerStore.addTrainer(workingTrainer.value);
		}
		// eslint-disable-next-line require-atomic-updates
		editTrainerId.value = null;
		addNewTrainer.value = false;
		errMessages.value = "";
	} catch (e: any) {
		console.error(e);

		if( e.toString().includes("Invalid email")) {
			errMessages.value = "Invalid Email entered";

		} else if( e.toString().includes("Error: User with that email does not exist!")) {
			errMessages.value = "Error: User with that email does not exist";
		} else {
			errMessages.value = `Unknown error: ${e}`;
		}
	} finally {
		trainerWorking.value = false;
	}
}

const back = () => {
	trainerWorking.value = false;
	addNewTrainer.value  = false;
	errMessages.value = "";
};

</script>

<style lang="scss">
#manage-calendar-modal {
	width: clamp(30rem, 80%, 45rem);
	min-height: 24rem;
	padding: 1rem;
	background: $color-white;
	box-shadow: 4px 4px 0 0 $color-gray-33;

	&.grid{
		display: grid;
		grid-template-rows: 3rem 1fr minmax(1.5rem, 10rem) minmax(1.5rem, 2.5rem);
		grid-template-columns: 100%;
		grid-auto-flow: column;
	}

	.col, .row{
		display: flex;
		flex-direction: column;
	}

	.row{
		flex-direction: row;
	}

	.tabs.row{
		gap: 2rem;
		justify-content: center;
	}

	.cheat{
		position: absolute;
		top: 5rem;
		left: 50%;
		background-color: white;
	}

	h1 {
		// padding-bottom: 0.5rem;
		margin: unset;
		font-size: 1.25rem;
		text-align: center;
		border-bottom: 1px solid $color-black;
	}

	input[type=radio] {margin-right: 1rem;}

	header {
		display: flex;
		gap: 0.5rem;
		place-content: center;
		border-bottom: 1px solid $color-gray-cc;

		.header-option:hover {
			cursor: pointer;
			background: $color-gray-cc;
		}

		.selected {text-decoration: underline;}
	}

	ul {list-style-type: none;}
	.buttons {margin-bottom: .25rem;}

	svg.icon{
		width:  1em;
		height: 1em;
		margin-left: 5px;

		.path.fill{
			border-radius: 2px;
			transition: all 250ms;
		}
	}

	button.icon.white, button.icon.black{
		width: fit-content;
		height:  fit-content;
		padding: unset;

		svg.icon{
			margin: unset;
		}
	}

	.buttons{
		display: flex;
		flex-direction: row;
		justify-content: space-between;
	}

	.trainer-table{
		height: fit-content;
		margin-block: 1rem;

		input{
			width: 100%;
			height: 38px;
			min-height: 24px;
			padding-inline: 0.25rem;
		}

		.header, .table.row{
			display: grid;
			grid-template-columns: 18rem 8rem 10rem 3rem;
			grid-auto-flow: column;
			justify-content: space-around;
			padding-inline: 1rem;
			vertical-align: middle;
		}

		.table.row{
			border-top: 1px solid $color-black;
		}

		.table.row .row{
			align-items: center;
			width: 100%;
			padding-block: 0.25rem;
		}

		.header{height: 1.5em;}

		.em{
			min-width: 20rem;
		}

		.nn{
			width: 8rem;
			text-wrap: nowrap;
		}

		.cc.row,
		.cc.row input{
			max-width: 10rem;
		}

		.bt, .bt div{
			display: flex;
			flex-direction: row;
			gap: 0.25rem;
			width: 3rem;
		}
	}

	.err-messages{
		p{
			color: $color-red;
		}
	}
}
</style>